import { Fragment, ReactElement } from 'react';

import { Text } from '../../../node/text';
import type { TextElementType } from '../../../types/text';
import {
  getCachedTokenSize,
  Size,
} from '../tokenMeasureCache/tokenMeasureCache';

export type Offset = {
  x: number;
  y: number;
};

export type State = {
  offset: Offset;
};

export type EventHandlerArgs = {
  element: TextElementType;
  sizes: Size[];
  isLastElement: boolean;
};

export type EventHandlerReturn = {
  elements: TextElementType[];
  offset: Offset;
};

export type BaseTextParserEventHandler = (
  args: EventHandlerArgs,
) => EventHandlerReturn;

export type BaseTextParserFragmentElement = ReactElement<
  { children: TextElementType[] },
  typeof Fragment
>;
export type BaseTextParserElements =
  | TextElementType
  | BaseTextParserFragmentElement
  | TextElementType[]
  | BaseTextParserFragmentElement[];

export type BaseTextParserArgs = {
  offset: Offset;
  elements: BaseTextParserElements;
  width: number;
  onTextExceedsWidth: BaseTextParserEventHandler;
  onTextFits: BaseTextParserEventHandler;
};

const flattenFragments = (elements: ReactElement[]): TextElementType[] =>
  elements.reduce<TextElementType[]>((acc, element) => {
    if (element.type === Fragment) {
      acc.push(...flattenFragments(element.props.children));
    }

    if (element.type === Text) {
      acc.push(element as TextElementType);
    }

    return acc;
  }, []);

export const baseTextParser = ({
  offset,
  elements,
  width,
  onTextExceedsWidth,
  onTextFits,
}: BaseTextParserArgs): TextElementType[] => {
  const state: State = {
    offset: { ...offset },
  };

  const elementsAsArray = Array.isArray(elements) ? elements : [elements];

  return flattenFragments(elementsAsArray).reduce<TextElementType[]>(
    (acc, element, index, array) => {
      const isLastElement = index === array.length - 1;
      const { props } = element;
      const { text = '' } = props;
      const tokenSizes: Size[] = [];

      if (text.length === 0) {
        return acc;
      }

      for (const token of text) {
        const { width: lastTokenWidth = 0, height: lastTokenHeight = 0 } =
          tokenSizes[tokenSizes.length - 1] || {};
        const { width: tokenWidth, height: tokenHeight } = getCachedTokenSize(
          token,
          props,
        );

        tokenSizes.push({
          width: tokenWidth + lastTokenWidth,
          height: Math.max(tokenHeight, lastTokenHeight),
        });

        if (tokenWidth + lastTokenWidth + state.offset.x > width) {
          const { elements, offset } = onTextExceedsWidth({
            element,
            sizes: tokenSizes,
            isLastElement,
          });

          state.offset = { ...offset };
          acc.push(...elements);

          return acc;
        }
      }

      const { elements, offset } = onTextFits({
        element,
        sizes: tokenSizes,
        isLastElement,
      });

      state.offset = { ...offset };
      acc.push(...elements);

      return acc;
    },
    [],
  );
};
